import { providerKindLabel } from '../utils/lookingFor'
import { ListChecks, LoaderCircle, LocateFixed, MapPin, Network, Search, ShieldCheck, SlidersHorizontal } from 'lucide-react'
import { VerifiedBadge } from '../components/VerifiedBadge'
import { offeringLines } from '../utils/offeringLines'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiErrorMessage, isAbortError } from '../api/client'
import { connectionsApi, discoveryApi, needsApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { ErrorMessage } from '../components/ErrorMessage'
import { HiddenReasons } from '../components/map/HiddenReasons'
import { ProviderDialog } from '../components/map/ProviderDialog'
import { ProviderMap } from '../components/map/ProviderMap'
import { ResultCountSummary } from '../components/ResultCountSummary'
import { TutorMapView } from './map/TutorMapView'
import { useConnectionGate } from '../hooks/useConnectionGate'
import { useContactConsent } from '../hooks/useContactConsent'
import { useFocusedFromNavigation } from '../hooks/useFocusedFromNavigation'
import { useMapCentre } from '../hooks/useMapCentre'
import { NEED_CATEGORY_LABELS } from '../utils/taxonomy'
import type {
  LearnerNeedResponse,
  MapProviderResponse,
  MapSearchResponse,
  NeedCategory,
  ParticipantCardResponse,
} from '../types/api'

const RADII: { label: string; km: number }[] = [
  { label: '2 km', km: 2 },
  { label: '5 km', km: 5 },
  { label: '10 km', km: 10 },
  { label: '25 km', km: 25 },
  { label: '50 km', km: 50 },
]
const RATINGS: { label: string; value: number | null }[] = [
  { label: 'Any', value: null },
  { label: '3 ★', value: 3 },
  { label: '4 ★', value: 4 },
  { label: '4.5 ★', value: 4.5 },
  { label: '5 ★', value: 5 },
]
const TEACHERS: { label: string; value: Verification }[] = [
  { label: 'All', value: 'all' },
  { label: 'Verified', value: 'verified' },
  { label: 'Not verified', value: 'unverified' },
]

type Verification = 'all' | 'verified' | 'unverified'

function chipClass(active: boolean) {
  return `chip ${active ? 'chip-active' : ''}`
}

export function MapPage() {
  const { profile } = useAuth()

  // A provider's map is the mirror image of a learner's: it shows who needs teaching, not who
  // offers it. Same component would mean a page of conditionals, so they are separate views.
  if (profile?.persona === 'SUPPLY') return <TutorMapView />

  return <LearnerMapView />
}

function LearnerMapView() {
  const { profile } = useAuth()
  const [radiusKm, setRadiusKm] = useState(5)
  const [minRating, setMinRating] = useState<number | null>(null)
  const [verification, setVerification] = useState<Verification>('all')
  const [query, setQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  // Which of the learner's own needs to show the map for - unset shows every need at once, same
  // as before this existed. Fetched once: a need added or removed mid-visit is rare enough that
  // reopening Filters to see the new list is fine.
  const [needs, setNeeds] = useState<LearnerNeedResponse[]>([])
  const [needCategory, setNeedCategory] = useState<NeedCategory | null>(null)

  useEffect(() => {
    needsApi.listOwn().then(setNeeds).catch(() => null)
  }, [])
  // Use the details you and the provider both filled in (mode, budget, target year, ...). A detail
  // only one of you stated never excludes anyone. Off until the learner turns it on: the map opens
  // on everyone nearby, and matching on details is a narrowing they choose.
  const [matchAttributes, setMatchAttributes] = useState(false)

  const [result, setResult] = useState<MapSearchResponse | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [openDetail, setOpenDetail] = useState(false)
  const [connecting, setConnecting] = useState(false)
  // Who may be connected with, and who already has been - see useConnectionGate.
  const gate = useConnectionGate()
  const consent = useContactConsent()
  const [notice, setNotice] = useState<string | null>(null)
  // Opens on a live GPS fix when the browser will give one, falling back to their saved
  // coordinates, or their pincode resolved once and saved back. See useMapCentre.
  const {
    centre: coords,
    locating,
    locatingFor,
    source: coordsSource,
    requestCurrentLocation,
    requestSavedLocation,
    locationError,
  } = useMapCentre()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Filters and keystrokes both retrigger the search, and answers can come back out of order.
  // Each run aborts the one before it, so the map always shows the newest query's result.
  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(() => {
      setLoading(true)
      // The people come from the network and every filter is applied to them by the server - see
      // DiscoveryService.search - so this is one search, not two.
      discoveryApi
        .providers(
          {
            lat: coords?.lat,
            lng: coords?.lng,
            radiusKm,
            verification,
            minRating: minRating ?? undefined,
            q: query.trim() || undefined,
            category: needCategory ?? undefined,
            matchAttributes,
          },
          controller.signal,
        )
        .then((res) => {
          setResult(res)
          setError(null)
          setLoading(false)
        })
        .catch((err) => {
          // A superseded search is not an error, and its loading state belongs to its successor.
          if (isAbortError(err)) return
          setError(apiErrorMessage(err, 'Could not load the map'))
          setLoading(false)
        })
    }, query ? 350 : 0)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [coords, radiusKm, verification, minRating, query, needCategory, matchAttributes])

  // Arriving from a profile recommendation's Connect: open that provider's card straight away.
  useFocusedFromNavigation(result?.providers, (id) => {
    setSelectedId(id)
    setOpenDetail(true)
  })

  async function connect(provider: MapProviderResponse, card: ParticipantCardResponse | null) {
    setError(null)
    // Contact details are shared once they accept - the sender agrees to that up front.
    const agreed = await consent.ask('initiate', 'provider')
    if (agreed === false) return
    setConnecting(true)
    try {
      await connectionsApi.create({
        receiverParticipantId: provider.id,
        aboutCategory: card?.services[0]?.category,
        contactConsent: agreed,
      })
      // Re-read the gate so the Connect button for this provider does not come back on the next
      // render - the request now exists, even if it is only sitting with a parent.
      gate.refresh()
      setNotice('Request sent. Their contact details appear under Connections once they accept.')
      setOpenDetail(false)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the connection request'))
    } finally {
      setConnecting(false)
    }
  }

  // Distinct, in the order the learner first stated them - two needs in the same category would
  // otherwise show the same chip twice.
  const needCategories = [...new Set(needs.map((n) => n.category))]
  // Named only when it is unambiguous - the one category filtered on, or the one category every
  // need shares - never a guess at which of several the person means.
  const categoryLabel = needCategory
    ? NEED_CATEGORY_LABELS[needCategory]
    : needCategories.length === 1
      ? NEED_CATEGORY_LABELS[needCategories[0]]
      : null
  const providers = result?.providers ?? []
  // Memoized on the actual coordinates, not recreated fresh every render: ProviderMap re-centres
  // whenever this object's identity changes, and a plain object literal here is a new identity on
  // every render - so clicking a dot (which re-renders this page for unrelated state) was
  // snapping the map back to the search origin, undoing any pan or zoom the person had just done.
  const centre = useMemo(
    () =>
      result && (result.centreLat !== 0 || result.centreLng !== 0)
        ? { lat: result.centreLat, lng: result.centreLng }
        : null,
    [result?.centreLat, result?.centreLng],
  )
  const selected = providers.find((p) => p.id === selectedId) ?? null
  // Every match, not the first five: the map says "22 listings" and the list beside it used to
  // show five, so the other seventeen existed only as dots you had to find by clicking. The API
  // returns them sorted by distance, so the nearest are still the ones you see first - the rest
  // are a scroll away rather than missing.
  const nearest = providers
  const wider = RADII.find((r) => r.km > radiusKm) ?? null

  return (
    <div className="space-y-4">
      {consent.dialog}
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
          <MapPin size={18} className="text-sun-500" /> YellowDots Map
        </h1>
        <p className="text-sm text-ink-500">
          Tutors, coaching and centres near you who teach{' '}
          {categoryLabel ? (
            <strong className="text-ink-900">{categoryLabel}</strong>
          ) : (
            'the category and subject on your profile'
          )}
          . Look for the Verified label.
          {!loading && result && (
            <>
              <ResultCountSummary
                shown={providers.length}
                totalInRadius={result.totalInRadius}
                networkTotal={result.networkTotal}
                moreOnNetwork={result.moreOnNetwork}
                radiusKm={result.radiusKm}
                label="providers"
              />
              {/* The city/state on the profile belong to the saved location, not wherever a live
                  GPS fix happens to be - shown only when that is actually what the map is using,
                  so it never names a place the results and the "You" dot are not really near. */}
              {coordsSource === 'saved' && profile?.city && profile?.state && (
                <>
                  {' '}in <strong className="text-ink-900">{profile.city}, {profile.state}</strong>
                </>
              )}
              {coordsSource && (coordsSource === 'gps' ? ', based on your live location' : ', based on your saved location')}
              .
              {wider && (
                <button
                  onClick={() => setRadiusKm(wider.km)}
                  className="ml-1 font-semibold text-amber-700 underline-offset-4 hover:underline"
                >
                  Widen to {wider.label}
                </button>
              )}
            </>
          )}
        </p>
        {loading && (
          <div className="chip chip-active mt-2 w-fit text-sm font-semibold">
            <LoaderCircle size={16} className="animate-spin" /> Searching for providers near you...
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input !pl-9"
            placeholder="Search subject or name"
            aria-label="Search subject or name"
          />
        </label>
        <button onClick={() => setShowFilters((s) => !s)} className="btn-ghost shrink-0">
          <SlidersHorizontal size={15} /> Filters
        </button>
        <button
          onClick={() => setMatchAttributes((m) => !m)}
          className={matchAttributes ? 'chip chip-active shrink-0' : 'btn-ghost shrink-0'}
          title="Only show providers whose details agree with yours on everything you both filled in"
        >
          <ListChecks size={15} /> Match my details
        </button>
        {/* Two buttons, not a single toggle, so which one the map is actually using is always
            visible at a glance - the selected one is highlighted the same way a radius or rating
            chip is below. */}
        <button
          onClick={requestCurrentLocation}
          disabled={locating}
          className={`${chipClass(coordsSource === 'gps')} shrink-0 disabled:cursor-wait disabled:opacity-70`}
          title="Search near where you are right now"
        >
          <LocateFixed size={15} /> {locatingFor === 'gps' ? 'Locating...' : 'Live location'}
        </button>
        <button
          onClick={() => requestSavedLocation()}
          disabled={locating}
          className={`${chipClass(coordsSource === 'saved')} shrink-0 disabled:cursor-wait disabled:opacity-70`}
          title="Search near your saved address"
        >
          <MapPin size={15} /> {locatingFor === 'saved' ? 'Locating...' : 'Saved location'}
        </button>
      </div>

      {/* Results come from the network. `native` means it could not be reached and our own list stood
          in for it - said plainly, because that list can include people the network does not have,
          who cannot be connected with until it is back. Where the network is not set up at all
          (networkConfigured false), our own list is simply how this deployment runs - no warning. */}
      {result?.source === 'native' && result.networkConfigured && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Network size={15} />
          The network could not be reached, so this is our own list. Connecting may not work until it is back.
        </div>
      )}

      {/* Providers left out because a detail you both filled in disagrees. Said with a way back,
          because a detail only one side stated never hides anyone - this is a real mismatch, but the
          learner may still want to see them. */}
      {matchAttributes && result && result.hiddenByAttributes > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
          <span className="flex flex-col gap-1.5">
            <span className="flex items-center gap-2">
              <ListChecks size={15} />
              {result.hiddenByAttributes} nearby provider{result.hiddenByAttributes === 1 ? ' is' : 's are'} hidden
              because their details differ from yours:
            </span>
            <HiddenReasons reasons={result.hiddenReasons} who="you chose" />
          </span>
          <button
            onClick={() => setMatchAttributes(false)}
            className="shrink-0 font-semibold underline-offset-4 hover:underline"
          >
            Show them
          </button>
        </div>
      )}

      {/* Said once, at the top, rather than only as a tooltip on each dead button - otherwise the
          buttons look broken and there is nothing on screen explaining why. */}
      {gate.blockedPendingGuardian && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sun-400/60 bg-sun-400/10 px-3 py-2 text-sm text-amber-900">
          <span className="flex items-center gap-2">
            <ShieldCheck size={15} />
            You can browse the map, but connecting needs a parent. Once they accept your request,
            the Connect buttons turn on.
          </span>
          <Link to="/guardian" className="shrink-0 font-semibold underline-offset-4 hover:underline">
            Invite a parent
          </Link>
        </div>
      )}

      {showFilters && (
        <div className="card-pad">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">Filters</div>
          {/* One row of groups on a wide screen, stacked once there is no room for three. */}
          <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:gap-x-10 md:gap-y-4">
            {/* Only shown once there is something to narrow - a single need gets the same map
                either way, and the choice would just be clutter. */}
            {needCategories.length > 1 && (
              <div>
                <span className="label">My needs</span>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setNeedCategory(null)} className={chipClass(needCategory === null)}>
                    All
                  </button>
                  {needCategories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNeedCategory(c)}
                      className={chipClass(needCategory === c)}
                    >
                      {NEED_CATEGORY_LABELS[c]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <span className="label">Minimum rating</span>
              <div className="flex flex-wrap gap-2">
                {RATINGS.map((r) => (
                  <button
                    key={r.label}
                    onClick={() => setMinRating(r.value)}
                    className={chipClass(minRating === r.value)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="label">Teachers</span>
              <div className="flex flex-wrap gap-2">
                {TEACHERS.map((t) => (
                  <button
                    key={t.label}
                    onClick={() => setVerification(t.value)}
                    className={chipClass(verification === t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="label">Search radius</span>
              <div className="flex flex-wrap gap-2">
                {RADII.map((r) => (
                  <button
                    key={r.label}
                    onClick={() => setRadiusKm(r.km)}
                    className={chipClass(radiusKm === r.km)}
                    title="As the crow flies - the distance shown per listing is by road, and can be a little more"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {/* A hover title alone never reaches a touch screen - this is the same note, always
                  on, so the radius-vs-shown-distance difference (straight line vs by road) isn't
                  mobile-invisible. */}
              <p className="mt-1.5 text-xs text-ink-400">
                Radius is a straight line; the distance on each listing is by road, so it can be a
                little more.
              </p>
            </div>
          </div>
        </div>
      )}

      <ErrorMessage message={error ?? locationError} />
      {notice && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card overflow-hidden lg:col-span-2">
          <ProviderMap
            centre={centre}
            radiusKm={result?.radiusKm ?? radiusKm}
            dots={providers.map((p) => ({
              id: p.id,
              lat: p.lat,
              lng: p.lng,
              name: p.fullName,
              distanceKm: p.distanceKm,
              subtitles: offeringLines(p.offerings, p.categories[0], p.subjects),
              tone: 'provider' as const,
              averageRating: p.averageRating,
              ratingCount: p.ratingCount,
              verified: p.verificationStatus === 'VERIFIED',
              fourCScored: p.fourCScored,
              aiCertified: p.aiCertified,
              blueRing: p.blueRing,
              ringLabel: p.ringLabel,
            }))}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id)
              setOpenDetail(true)
            }}
            countLabel={`${providers.length} listing${providers.length === 1 ? '' : 's'}`}
          />
        </div>

        <div className="space-y-4">
          <div className="card-pad">
            {selected ? (
              <ProviderDetail
                provider={selected}
                onOpen={() => setOpenDetail(true)}
                existingLabel={gate.labelFor(selected.id)}
              />
            ) : (
              <>
                <p className="text-sm text-ink-500">Tap a dot to see details.</p>
                <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-500">
                  <span>
                    <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-ink-900 align-middle" />
                    You
                  </span>
                  <span>
                    <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-sun-500 align-middle" />
                    Tutor / Centre
                  </span>
                </p>
              </>
            )}
          </div>

          <div className="card-pad">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wide text-ink-400">Nearest</span>
              {nearest.length > 0 && (
                <span className="text-[10px] font-semibold text-ink-400">
                  {nearest.length} {nearest.length === 1 ? 'listing' : 'listings'}
                </span>
              )}
            </div>
            {loading ? (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-500">
                <LoaderCircle size={14} className="animate-spin" /> Searching...
              </p>
            ) : !centre ? (
              <p className="mt-2 text-sm text-ink-500">
                Set your location to see who is near you.
              </p>
            ) : nearest.length === 0 ? (
              <p className="mt-2 text-sm text-ink-500">
                No providers teaching your category and subject are nearby yet.{' '}
                <button
                  onClick={() => {
                    setMinRating(null)
                    setVerification('all')
                    setQuery('')
                    setNeedCategory(null)
                  }}
                  className="font-semibold text-amber-700 underline-offset-4 hover:underline"
                >
                  Clear filters
                </button>{' '}
                or{' '}
                <Link to="/profile" className="font-semibold text-amber-700 underline-offset-4 hover:underline">
                  check what's on your profile
                </Link>
                .
              </p>
            ) : (
              // Capped and scrolled rather than growing without limit: at a 25 km radius this list
              // runs to dozens, and a card that tall pushes the map off the top of the screen.
              <ul className="mt-2 max-h-[26rem] space-y-2 overflow-y-auto pr-1">
                {nearest.map((p) => (
                  <li key={p.id}>
                    <button
                      onClick={() => setSelectedId(p.id)}
                      className={`w-full rounded-xl border px-3 py-2 text-left transition ${
                        p.id === selectedId
                          ? 'border-sun-400 bg-sun-400/10'
                          : 'border-ink-200 hover:bg-ink-50'
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="flex min-w-0 flex-wrap items-center gap-1.5 break-words text-sm font-semibold text-ink-900">
                          {p.fullName}
                          <VerifiedBadge status={p.verificationStatus} size="xs" />
                        </span>
                        <span className="shrink-0 text-xs text-ink-500">{p.distanceKm} km</span>
                      </div>
                      {p.specialisation && (
                        <span className="block text-xs text-ink-500">{p.specialisation}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {selected && openDetail && (
        <ProviderDialog
          provider={selected}
          connecting={connecting}
          onClose={() => setOpenDetail(false)}
          onConnect={(card) => connect(selected, card)}
          connectBlockedReason={gate.blockedReason}
          existingLabel={gate.labelFor(selected.id)}
          connectError={error}
        />
      )}
    </div>
  )
}

function ProviderDetail({
  provider,
  onOpen,
  /** "Connected" / "Request sent" / "Waiting for parent approval", when one already exists. */
  existingLabel,
}: {
  provider: MapProviderResponse
  onOpen: () => void
  existingLabel?: string | null
}) {
  return (
    <div>
      <div className="break-words text-lg font-bold text-ink-900">{provider.fullName}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-ink-500">
        <span>{providerKindLabel(provider.entityKind)}</span>
        <VerifiedBadge status={provider.verificationStatus} />
      </div>

      {provider.categories.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {provider.categories.map((c) => (
            <span key={c} className="pill bg-sun-400/20 text-amber-800">{c}</span>
          ))}
        </div>
      )}
      {provider.subjects.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {provider.subjects.slice(0, 6).map((s) => (
            <span key={s} className="pill bg-ink-100 text-ink-700">{s}</span>
          ))}
        </div>
      )}

      <dl className="mt-3 space-y-1 text-sm">
        {provider.specialisation && (
          <div>
            <dt className="inline font-semibold text-ink-900">About: </dt>
            <dd className="inline text-ink-600">{provider.specialisation}</dd>
          </div>
        )}
        {provider.averageRating != null && (
          <div>
            <dt className="inline font-semibold text-ink-900">Rating: </dt>
            <dd className="inline text-ink-600">
              {provider.averageRating} ({provider.ratingCount})
            </dd>
          </div>
        )}
        <div>
          <dt className="inline font-semibold text-ink-900">Distance: </dt>
          <dd className="inline text-ink-600">~{provider.distanceKm} km</dd>
        </div>
      </dl>

      {/* The button stays, because the profile is still worth opening once you are connected -
          that is when contact details are there to read. What changes is the promise it makes:
          "& connect" is over, and the line about nothing being sent yet is about a request that
          has already been sent and accepted. The dialog behind this button says the same thing
          the same way, so the card and the dialog cannot disagree about whether you are
          connected. */}
      <button onClick={onOpen} className="btn-sun mt-3 w-full">
        {existingLabel ? 'View profile' : 'View profile & connect'}
      </button>
      {existingLabel ? (
        <p className="mt-2 text-center">
          <span className="rounded-full bg-ink-100 px-3 py-1 text-xs font-semibold text-ink-600">
            {existingLabel}
          </span>
        </p>
      ) : (
        <p className="mt-2 text-center text-xs text-ink-400">
          You&apos;ll see their full profile before anything is sent.
        </p>
      )}
    </div>
  )
}
